import type { ReactNode } from "react";
import { create } from "zustand";

import { DEFAULT_TAB_ID } from "./navBarStore";

/**
 * The leave guard's state, without its dialog (`leaveGuard.tsx` draws that): which tabs hold
 * unsaved changes, the saves an account change runs first, and the one question pending. Plain
 * modules -- a desktop command, a native menu -- ask through `requestLeave` here without pulling
 * in the dialog's UI.
 */

/** A page's own question, for losses that are not unsaved edits (an upload
 *  still sending): its title, what is lost, and the two answers. */
export interface LeavePrompt {
    title: string;
    message: ReactNode;
    leaveLabel: string;
    stayLabel: string;
}

interface PendingLeave {
    go: () => void;
    message: ReactNode;
    /** Set when the page holding the leave asked its own question. */
    prompt?: LeavePrompt;
}

interface LeaveGuardState {
    pending: PendingLeave | null;
    /** The mounted `LeaveGuardHost`s, in mount order; the first draws the dialog. */
    hosts: readonly symbol[];
}

export const useLeaveGuardStore = create<LeaveGuardState>(() => ({ pending: null, hosts: [] }));

/** Per tab, each hold's count of changes (undefined: some, uncounted). */
const holds = new Map<string, Map<symbol, number | undefined>>();
/** Holds that ask their own question, read when the leave is asked. */
const prompts = new Map<symbol, () => LeavePrompt>();
/** Set while a discarded leave runs, so the navigation it starts does not ask again. */
let discarding = false;
/** The `saveBeforeAccountChange` saves, in the order they registered. */
const savesBeforeAccountChange = new Set<{ save: () => Promise<unknown> }>();
/** Set while an account change waits for those saves. */
let savingForAccountChange = false;
/** How long an account change waits for one of those saves before it asks instead: a request
 *  that never answers must not hold every later switch. */
export const ACCOUNT_CHANGE_SAVE_TIMEOUT_MS = 30_000;

function savedInTime(save: Promise<unknown>): Promise<unknown> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<never>((_resolve, reject) => {
        timer = setTimeout(
            () => reject(new Error("The save did not finish in time")),
            ACCOUNT_CHANGE_SAVE_TIMEOUT_MS,
        );
    });
    return Promise.race([save, late]).finally(() => clearTimeout(timer));
}

/** A document navigation invoked by the confirmed discard already has consent. */
export function isDiscardingChanges(): boolean {
    return discarding;
}

function heldIn(scope: string): (number | undefined)[] {
    return Array.from(holds.get(scope)?.values() ?? []);
}

/** Takes a hold on `scope`'s unsaved changes until the returned release runs.
 *  `prompt`, when given, is the question asked instead of "Leave without
 *  saving?"; it is read at the moment someone tries to leave. */
export function holdUnsavedChanges(
    scope: string,
    changes?: number,
    prompt?: () => LeavePrompt,
): () => void {
    const id = Symbol(scope);
    const scoped = holds.get(scope) ?? new Map<symbol, number | undefined>();
    holds.set(scope, scoped.set(id, changes));
    if (prompt) prompts.set(id, prompt);
    return () => {
        scoped.delete(id);
        prompts.delete(id);
        if (scoped.size === 0 && holds.get(scope) === scoped) holds.delete(scope);
    };
}

/** The first hold in `scope` that asks its own question, if any. */
function promptIn(scope: string): LeavePrompt | undefined {
    for (const id of holds.get(scope)?.keys() ?? []) {
        const prompt = prompts.get(id);
        if (prompt) return prompt();
    }
    return undefined;
}

/** Whether a page in `scope` (the web window when omitted) has unsaved changes. */
/** Whether a leave is waiting on the discard question: a later leave must not take its place. */
export function isLeaveQuestionPending(): boolean {
    return useLeaveGuardStore.getState().pending !== null;
}

export function hasUnsavedChanges(scope: string = DEFAULT_TAB_ID): boolean {
    return heldIn(scope).length > 0;
}

/** Whether anything in this page holds unsaved changes, in any scope. */
export function hasAnyUnsavedChanges(): boolean {
    return holds.size > 0;
}

/** What the dialog says is lost: the count when every hold counts its changes. */
export function lostMessage(counts: readonly (number | undefined)[]): string {
    if (counts.length === 0 || counts.some((count) => count === undefined))
        return "Your unsaved changes will be lost.";
    const total = counts.reduce<number>((sum, count) => sum + (count ?? 0), 0);
    return `${total} unsaved ${total === 1 ? "change" : "changes"} will be lost.`;
}

export interface LeaveRequest {
    /** The tab being left; the web window when omitted. */
    scope?: string;
    /** Ask even without a hold: the page says it is dirty. */
    dirty?: boolean;
    /** What would be lost; the holds' count otherwise. */
    message?: ReactNode;
}

/**
 * Leaves the page shown in `scope`: `go` runs at once while nothing there is unsaved, and
 * otherwise once the user discards the changes. With no host mounted to ask, `go` runs at once:
 * the guard never blocks a navigation it cannot put to the user.
 */
export function requestLeave(go: () => void, request: LeaveRequest = {}): void {
    const scope = request.scope ?? DEFAULT_TAB_ID;
    const counts = heldIn(scope);
    const unsaved = request.dirty === true || counts.length > 0;
    if (discarding || !unsaved || useLeaveGuardStore.getState().hosts.length === 0) {
        go();
        return;
    }
    const prompt = request.message === undefined ? promptIn(scope) : undefined;
    useLeaveGuardStore.setState({
        pending: { go, message: request.message ?? prompt?.message ?? lostMessage(counts), prompt },
    });
}

/**
 * Registers the save of a page that saves on its way out instead of taking a hold (the file
 * viewer's edit modes, on unmount). An account change unmounts the page only once the other
 * account is signed in, too late to send as the one that made the changes, so
 * `requestAccountChange` runs the save first. Returns its unregister.
 */
export function saveBeforeAccountChange(save: () => Promise<unknown>): () => void {
    const entry = { save };
    savesBeforeAccountChange.add(entry);
    return () => {
        savesBeforeAccountChange.delete(entry);
    };
}

/**
 * `requestLeave` for a leave that signs another account in (a project switch). The
 * `saveBeforeAccountChange` saves run first, one after another, while the old account is still
 * signed in; one that fails or misses `ACCOUNT_CHANGE_SAVE_TIMEOUT_MS` makes the leave ask as for
 * unsaved changes. A change asked while another waits is dropped. Settles once it went or asked.
 */
export async function requestAccountChange(
    go: () => void,
    request: LeaveRequest = {},
): Promise<void> {
    if (savesBeforeAccountChange.size === 0) {
        requestLeave(go, request);
        return;
    }
    if (savingForAccountChange) return;
    savingForAccountChange = true;
    const saved = await runSavesBeforeAccountChange();
    savingForAccountChange = false;
    requestLeave(go, saved ? request : { ...request, dirty: true });
}

/**
 * Runs the `saveBeforeAccountChange` saves, one after another, each bounded by
 * `ACCOUNT_CHANGE_SAVE_TIMEOUT_MS`; true when every one saved. For an account change, and for a
 * host that hides the page without leaving it (a desktop project page another one replaced).
 */
export async function runSavesBeforeAccountChange(): Promise<boolean> {
    let saved = true;
    for (const { save } of [...savesBeforeAccountChange]) {
        try {
            await savedInTime(save());
        } catch (error) {
            console.error("[LeaveGuard] A page could not save before the account change:", error);
            saved = false;
        }
    }
    return saved;
}

export function keepEditing() {
    useLeaveGuardStore.setState({ pending: null });
}

export function discardAndLeave() {
    const { pending } = useLeaveGuardStore.getState();
    useLeaveGuardStore.setState({ pending: null });
    if (!pending) return;
    discarding = true;
    try {
        pending.go();
    } finally {
        discarding = false;
    }
}
